// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './ZoneFrame.module.css';
import { computeZoneLayout, Rect } from './zone-frame';

interface ZoneFrameProps {
  /** Fixed top-sliver height. */
  sliverHeight?: number;
  /** Left Tools rail width; 0 hides it. */
  toolsWidth?: number;
  /** Right Activities rail width (drag-resizable); 0 hides it. */
  activitiesWidth: number;
  /** Bottom Results band height (drag-resizable); 0 hides it. */
  resultsHeight: number;
  /** Fired while dragging a resize handle; the Editor owns the sizes and clamps
   *  them through computeZoneLayout, so this reports the raw requested size. */
  onResize?: (next: { activitiesWidth?: number; resultsHeight?: number }) => void;
  sliver: React.ReactNode;
  tools?: React.ReactNode;
  model: React.ReactNode;
  activities?: React.ReactNode;
  results?: React.ReactNode;
}

interface ZoneFrameState {
  width: number;
  height: number;
}

/**
 * The WS8a zone frame (imperative shell over zone-frame.ts): renders the five
 * zones as bounded, non-overlapping regions and owns only the resize gesture.
 * The layout math — including the Model-never-covered and resize-clamp
 * invariants — lives in computeZoneLayout, so this component just measures the
 * container (ResizeObserver) and paints each child at its zone rect. Every
 * surface lives inside a zone here rather than floating at an absolute offset,
 * which is the structural fix for the overlap class of bug (NC-1).
 */
export class ZoneFrame extends React.PureComponent<ZoneFrameProps, ZoneFrameState> {
  private readonly el = React.createRef<HTMLDivElement>();
  private ro?: ResizeObserver;

  private dragKind?: 'activities' | 'results';
  private dragStartClient = 0;
  private dragStartSize = 0;

  state: ZoneFrameState = { width: 0, height: 0 };

  componentDidMount(): void {
    const node = this.el.current;
    // jsdom has no ResizeObserver; guard so tests mount without a polyfill (the
    // frame then measures 0x0, which the pure layout handles gracefully).
    if (node && typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => {
        const r = node.getBoundingClientRect();
        this.setState({ width: r.width, height: r.height });
      });
      this.ro.observe(node);
    }
  }

  componentWillUnmount(): void {
    this.ro?.disconnect();
    this.endDrag();
  }

  private readonly onDragMove = (e: MouseEvent): void => {
    const { onResize } = this.props;
    if (!this.dragKind || !onResize) {
      return;
    }
    if (this.dragKind === 'activities') {
      // Handle is on the rail's left edge: dragging left (smaller clientX) grows it.
      onResize({ activitiesWidth: this.dragStartSize + (this.dragStartClient - e.clientX) });
    } else {
      // Handle is on the band's top edge: dragging up (smaller clientY) grows it.
      onResize({ resultsHeight: this.dragStartSize + (this.dragStartClient - e.clientY) });
    }
  };

  private readonly endDrag = (): void => {
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.endDrag);
    this.dragKind = undefined;
  };

  private startDrag(kind: 'activities' | 'results', clientPos: number, startSize: number, e: React.MouseEvent): void {
    if (!this.props.onResize) {
      return;
    }
    e.preventDefault();
    this.dragKind = kind;
    this.dragStartClient = clientPos;
    this.dragStartSize = startSize;
    document.addEventListener('mousemove', this.onDragMove);
    document.addEventListener('mouseup', this.endDrag);
  }

  render(): React.ReactNode {
    const { sliverHeight = 40, toolsWidth = 0, activitiesWidth, resultsHeight } = this.props;
    const layout = computeZoneLayout({
      width: this.state.width,
      height: this.state.height,
      sliverHeight,
      toolsWidth,
      activitiesWidth,
      resultsHeight,
    });

    const zone = (rect: Rect, className: string, children: React.ReactNode): React.ReactElement => (
      <div className={className} style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height }}>
        {children}
      </div>
    );

    return (
      <div ref={this.el} className={styles.frame}>
        {zone(layout.sliver, styles.sliver, this.props.sliver)}
        {toolsWidth > 0 ? zone(layout.tools, styles.tools, this.props.tools) : null}
        {zone(layout.model, styles.model, this.props.model)}
        {activitiesWidth > 0 ? (
          <>
            {zone(layout.activities, styles.activities, this.props.activities)}
            <div
              className={styles.activitiesHandle}
              style={{ left: layout.activities.x - 3, top: layout.activities.y, height: layout.activities.height }}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize activities"
              onMouseDown={(e) => this.startDrag('activities', e.clientX, activitiesWidth, e)}
            />
          </>
        ) : null}
        {resultsHeight > 0 ? (
          <>
            {zone(layout.results, styles.results, this.props.results)}
            <div
              className={styles.resultsHandle}
              style={{ left: layout.results.x, top: layout.results.y - 3, width: layout.results.width }}
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize results"
              onMouseDown={(e) => this.startDrag('results', e.clientY, resultsHeight, e)}
            />
          </>
        ) : null}
      </div>
    );
  }
}
